import { useContext, useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ScoringContext } from '@/contexts/scoring/context';
import { useEvaluationSources } from '@/service/hooks/useEvaluationSources';
import {
  AddEvaluationSourceMsg,
  DeleteEvaluationSourceMsg,
  SCORING_WORKER_HOST_ACTION,
  UpdateEvaluationSourceMsg,
} from '@/workers/scoring/types';
import { API_METHOD } from '@/service/constants';
import {
  COMMON_STRING,
  DELETE_CONFIRMATION_STRING,
  SUCCESS_STRING,
} from '@/constants/dictionary';
import { ARIA_LABEL } from '@/constants/ariaLabel';
import { ConfirmationButtonSet } from '../common/ConfirmationButtonSet';
import { MTDialog } from '../common/MTDialog';
import { EvaluationSourceDialog } from './EvaluationSourceDialog';
import { EvaluationSourcesTable } from './EvaluationTableSource/EvaluationSourcesTable';

export interface Source {
  link: string;
  comment: string;
}

export const EvaluationSources = () => {
  const {
    state: { fixtureId, collectionId },
    useDispatchWithResponse,
  } = useContext(ScoringContext);
  const { evaluationSources, isLoading, mutate } = useEvaluationSources();

  const [currentSource, setCurrentSource] = useState<Source>({
    link: '',
    comment: '',
  });
  const [initialSource, setInitialSource] = useState<Source | null>(null);
  const [editSourceId, setEditSourceId] = useState<string | null>(null);
  const [deleteSourceId, setDeleteSourceId] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isOpenConfirmation, setIsOpenDeleteConfirmation] = useState(false);

  const onAddClick = () => {
    setCurrentSource({ link: '', comment: '' });
    setInitialSource(null);
    setEditSourceId(null);
    setOpenDialog(true);
  };

  const { dispatch: addSource } =
    useDispatchWithResponse<AddEvaluationSourceMsg>(
      SCORING_WORKER_HOST_ACTION.ADD_EVALUATION_SOURCE,
    );

  const { dispatch: updateSource } =
    useDispatchWithResponse<UpdateEvaluationSourceMsg>(
      SCORING_WORKER_HOST_ACTION.UPDATE_EVALUATION_SOURCE,
    );

  const { dispatch: deleteSource } =
    useDispatchWithResponse<DeleteEvaluationSourceMsg>(
      SCORING_WORKER_HOST_ACTION.DELETE_EVALUATION_SOURCE,
    );

  const handleTableClick = (
    e: React.MouseEvent<HTMLTableSectionElement>,
  ): void => {
    const button = (e.target as HTMLElement).closest(
      'button[data-action]',
    ) as HTMLButtonElement | null;
    if (!button) return;

    const action = button.dataset.action;
    const sourceId = button.dataset.sourceId;
    if (!sourceId) return;

    const source = evaluationSources?.find((s) => s.sourceId === sourceId);
    if (!source) return;

    if (action === 'update') {
      setCurrentSource({ link: source.url, comment: source.comment });
      setInitialSource({ link: source.url, comment: source.comment });
      setEditSourceId(sourceId);
      setOpenDialog(true);
    } else if (action === 'delete') {
      setDeleteSourceId(sourceId);
      setIsOpenDeleteConfirmation(true);
    }
  };

  const onSubmitEvaluation = async (
    action: API_METHOD.POST | API_METHOD.PUT | API_METHOD.DELETE,
  ) => {
    const defaultRequestData = {
      fixtureId,
      collectionId,
    };

    if (action === API_METHOD.DELETE) {
      await deleteSource(
        { ...defaultRequestData, sourceId: deleteSourceId || '' },
        { successMessage: SUCCESS_STRING.SOURCE_SUCCESSFULLY_DELETED },
      );
      await mutate();
      setIsOpenDeleteConfirmation(false);
      return;
    }

    const payload = {
      ...defaultRequestData,
      url: currentSource.link,
      comment: currentSource.comment,
    };

    if (action === API_METHOD.POST) {
      await addSource(payload, {
        successMessage: SUCCESS_STRING.SOURCE_SUCCESSFULLY_ADDED,
      });
    } else if (action === API_METHOD.PUT) {
      await updateSource(
        { ...payload, sourceId: editSourceId || '' },
        { successMessage: SUCCESS_STRING.SOURCE_SUCCESSFULLY_UPDATED },
      );
    }
    await mutate();

    setOpenDialog(false);
    setEditSourceId(null);
    setCurrentSource({ link: '', comment: '' });
    setInitialSource(null);
  };

  const isSubmitDisabled = initialSource
    ? (currentSource.link.trim() === initialSource.link.trim() &&
        currentSource.comment.trim() === initialSource.comment.trim()) ||
      !currentSource.link.trim()
    : !currentSource.link.trim();

  if (isLoading) return <CircularProgress />;

  return (
    <>
      <Stack m={2}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography>{COMMON_STRING.WEB_LINKS}</Typography>
          <Button
            aria-label={ARIA_LABEL.ADD_LINK_BUTTON}
            variant='contained'
            onClick={onAddClick}
          >
            {`+ ${COMMON_STRING.ADD_LINK}`}
          </Button>
        </Stack>

        <EvaluationSourcesTable
          onTableClick={handleTableClick}
          data={evaluationSources || []}
        />
      </Stack>

      <EvaluationSourceDialog
        currentSource={currentSource}
        setCurrentSource={setCurrentSource}
        openDialog={openDialog}
        onSubmit={onSubmitEvaluation}
        onClose={() => setOpenDialog(false)}
        isSubmitDisabled={isSubmitDisabled}
        isEditMode={!!initialSource}
      />

      <MTDialog
        open={isOpenConfirmation}
        onClose={() => setIsOpenDeleteConfirmation(false)}
        title={COMMON_STRING.DELETE_SOURCE}
        maxWidth='xs'
      >
        <ConfirmationButtonSet
          shouldTitle={false}
          confirmMessage={DELETE_CONFIRMATION_STRING.SOURCE_EVALUATION}
          onClose={() => setIsOpenDeleteConfirmation(false)}
          onConfirm={onSubmitEvaluation}
        />
      </MTDialog>
    </>
  );
};
